/**
 * La tarjeta que explica el paso.
 *
 * Se coloca junto al elemento resaltado y, si no cabe, busca el lado que sí
 * —abajo, arriba, derecha, izquierda— y se recorta contra el borde de la
 * ventana. Sin objetivo, queda centrada.
 *
 * Es lo único interactivo de la guía, y por eso lleva `data-tour-ui`: sin
 * esa marca, `hasOpenDialog()` la contaría como un modal del panel y el
 * motor no navegaría nunca.
 *
 * **Toma el foco en cada paso, pero no lo atrapa.** Tomarlo es lo que hace
 * que un lector de pantalla anuncie el paso nuevo. Atraparlo sería
 * contradecir el resto del diseño: el velo deja pulsar la pantalla y hay
 * pasos que piden pulsar el elemento resaltado, así que encerrar el
 * tabulador dentro de la tarjeta dejaría a quien navega con teclado sin
 * poder hacer justo lo que el paso le pide.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Button } from '@/components/ui';
import { fadeIn, scalePop } from '@/lib/motion';
import { cn } from '@/utils/cn';
import type { TourPlacement } from '../types';
import type { TargetRect } from './useTargetRect';

const CARD_WIDTH = 360;
const GAP = 14;
const MARGIN = 16;

interface TourPopoverProps {
  title: string;
  body: string;
  stepNumber: number;
  stepCount: number;
  rect: TargetRect | null;
  placement?: TourPlacement;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
}

interface Position {
  top: number;
  left: number;
  centered: boolean;
}

function resolvePosition(
  rect: TargetRect | null,
  size: { width: number; height: number },
  preferred: TourPlacement = 'auto',
): Position {
  if (!rect) return { top: 0, left: 0, centered: true };

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const { width, height } = size;

  const fits: Record<Exclude<TourPlacement, 'auto'>, boolean> = {
    bottom: vh - (rect.top + rect.height) >= height + GAP + MARGIN,
    top: rect.top >= height + GAP + MARGIN,
    right: vw - (rect.left + rect.width) >= width + GAP + MARGIN,
    left: rect.left >= width + GAP + MARGIN,
  };

  const order: Exclude<TourPlacement, 'auto'>[] =
    preferred === 'auto'
      ? ['bottom', 'top', 'right', 'left']
      : [preferred, 'bottom', 'top', 'right', 'left'];

  // Si ninguno cabe se usa el primero de la lista y se recorta: mejor una
  // tarjeta pegada al borde que una fuera de la pantalla.
  const placement = order.find((side) => fits[side]) ?? order[0];

  let top: number;
  let left: number;

  switch (placement) {
    case 'top':
      top = rect.top - height - GAP;
      left = rect.left + rect.width / 2 - width / 2;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - height / 2;
      left = rect.left + rect.width + GAP;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - height / 2;
      left = rect.left - width - GAP;
      break;
    case 'bottom':
    default:
      top = rect.top + rect.height + GAP;
      left = rect.left + rect.width / 2 - width / 2;
      break;
  }

  return {
    top: Math.min(Math.max(top, MARGIN), Math.max(MARGIN, vh - height - MARGIN)),
    left: Math.min(Math.max(left, MARGIN), Math.max(MARGIN, vw - width - MARGIN)),
    centered: false,
  };
}

export function TourPopover({
  title,
  body,
  stepNumber,
  stepCount,
  rect,
  placement,
  isFirst,
  isLast,
  onNext,
  onPrev,
  onClose,
}: TourPopoverProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: CARD_WIDTH, height: 200 });
  const prefersReducedMotion = useReducedMotion();

  // El componente se remonta en cada paso (la clave lleva el id del paso),
  // así que este efecto de montaje mueve el foco una vez por paso.
  useEffect(() => {
    cardRef.current?.focus();
  }, []);

  // La altura depende del texto del paso, así que se mide tras pintar y
  // antes de que el navegador lo enseñe: colocar con una altura supuesta
  // hace que la tarjeta salte al primer render de cada paso.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    setSize((current) =>
      Math.abs(current.height - box.height) < 1 && Math.abs(current.width - box.width) < 1
        ? current
        : { width: box.width, height: box.height },
    );
  }, [title, body, rect]);

  const position = resolvePosition(rect, size, placement);

  return (
    <motion.div
      ref={cardRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby="tour-step-title"
      tabIndex={-1}
      data-tour-ui=""
      variants={prefersReducedMotion ? fadeIn : scalePop}
      initial="hidden"
      animate="visible"
      exit="exit"
      className={cn(
        'fixed z-[131] flex flex-col gap-3 rounded-xl border border-border bg-background p-4',
        'shadow-[var(--shadow-dialog)] outline-none',
      )}
      style={
        position.centered
          ? {
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: `min(${CARD_WIDTH}px, calc(100vw - ${MARGIN * 2}px))`,
            }
          : {
              top: position.top,
              left: position.left,
              width: `min(${CARD_WIDTH}px, calc(100vw - ${MARGIN * 2}px))`,
            }
      }
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Paso {stepNumber} de {stepCount}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar la guía"
          className="-m-1 cursor-pointer rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      <div>
        <h2
          id="tour-step-title"
          className="font-display text-[17px] tracking-tight text-foreground"
        >
          {title}
        </h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{body}</p>
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer text-[12px] text-muted-foreground transition-colors hover:text-foreground"
        >
          Salir
        </button>
        <div className="flex items-center gap-2">
          {!isFirst && (
            <Button variant="outlined" size="sm" onClick={onPrev}>
              Atrás
            </Button>
          )}
          <Button size="sm" onClick={onNext}>
            {isLast ? 'Terminar' : 'Siguiente'}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
